import React from "react";
import debounce from "lodash/debounce";
import asPage from "components/core/as-page";
import Breadcrumbs from "components/core/breadcrumbs";
import Header from "components/core/header";
import HeaderBanner from "components/core/header-banner";
import Layout from "components/core/layout";
import Link from "components/elements/link";
import PhoneLink from "components/elements/phone-link";
import Slider from "components/elements/slider";
import SVG from "components/elements/svg";
import Tabs from "components/elements/tabs";
import TextContent from "components/elements/text-content";
import CTASignUp from "components/partials/cta-sign-up";
import FeeCalculator from "components/partials/fee-calculator";
import Integrations from "components/partials/integrations";

const data = [
  {
    icon: "home-laptop",
    title: "Create account",
    content: (
      <p>
        Create a seller account by clicking below and follow our easy step by
        step process.
      </p>
    ),
  },
  {
    icon: "calculator",
    title: "Set Reserve Price",
    content: (
      <p>
        Agree Reserve Price (the asking price, normally based on valuations
        taken) with us. The property won't sell unless it meets or exceeds the
        Reserve Price. In the event that the price achieved falls short then you
        can always consider accepting the highest bid attained.
      </p>
    ),
  },
  {
    icon: "our-way-1",
    title: "Provide the relevant property details",
    content: (
      <p>
        Our sales support team will let you know what information is required.
        This will include proof of ownership and your I.D. Be rest assured that
        your information will only be shared with third parties if in the course
        of business (e.g. if you win an auction your details will need passing
        on to conveyancers)
      </p>
    ),
  },
  {
    icon: "our-way-2",
    title: "Compilation of the property listing",
    content: (
      <p>
        This is handled fully by the Hammer Price Homes sales support team. This
        includes professional photography, floorplans, EPC and the property
        description.
      </p>
    ),
  },
  {
    icon: "doors",
    title: "Marketing process",
    content: (
      <p>
        Once the listing is prepared and has your approval we will commence
        marketing. We market on our website, Rightmove and Zoopla and put up a
        property board. We market for 4 weeks and host open days so that
        prospective buyers can view. We host all buyer enquiries. The bidding
        window (The Auction) open for the last 3 days of this 4 weeks process.
      </p>
    ),
  },
  {
    icon: "sold",
    title: "Sell",
    content: (
      <p>
        At the end of the auction the highest bidder, so long as they have met
        or exceeded the reserve price wins the auction. Upon payment of our
        reservation fee we will issue the memorandum of sale to all parties,
        including the solicitors, starting the conveyancing process (up to 90
        days). We stay involved until the sale completes and you are in funds.
      </p>
    ),
  },
];

class HowItWorksSeller extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showSlider: false,
    };
    this.showSlider = debounce(this.showSlider.bind(this), 10);
  }

  componentDidMount() {
    this.showSlider();
    window.addEventListener("resize", this.showSlider);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.showSlider);
  }

  showSlider() {
    this.setState({ showSlider: window.innerWidth <= 768 });
  }

  changeSlide(interval) {
    this.slider.changeSlide(interval);
  }

  render() {
    const { user } = this.props;

    return (
      <Layout className="how-it-works-seller" title="How it works - Seller">
        <Header>
          <HeaderBanner
            title="How it works - Seller"
            icon="home"
            background="/static/images/header-how-it-works.jpg"
          />
        </Header>

        <Breadcrumbs>
          <Breadcrumbs.Item title="How it works" url="how-it-works" />
          <Breadcrumbs.Item title="Seller" url="how-it-works/seller" />
        </Breadcrumbs>

        <section className="how-it-works__intro">
          <div className="container">
            <div className="row">
              <div className="col-sm-12 col-intro">
                <h2 className="section-heading">Our fee promise.</h2>
                <TextContent>
                  <ul>
                    <li>
                      It is completely free to sell your property with Hammer
                      Price Homes. There are no agency fees, no catches, the
                      buyer pays a reservation fee on winning the auction.&nbsp;
                    </li>
                    <li>
                      No hidden fees guaranteed. It is completely free to
                      register, please follow the process below.
                    </li>
                  </ul>
                </TextContent>
              </div>
            </div>
          </div>
        </section>

        <section className="how-it-works__details">
          <div className="container">
            <div className="row">
              {!this.state.showSlider && (
                <div className="col-md-8">
                  {data.map((item, index) => (
                    <div className="how-it-works__point">
                      <div className="image">
                        <SVG src={item.icon} className="icon" />
                      </div>
                      <div className="content">
                        <p className="section-heading">
                          <span className="number">{index + 1}</span>
                          {item.title}
                        </p>

                        <TextContent>{item.content}</TextContent>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {this.state.showSlider && (
                <div className="col-xs-12">
                  <button
                    type="button"
                    onClick={() => this.changeSlide(-1)}
                    className="arrow arrow-left"
                  >
                    <SVG src="chevron-left" className="icon" />
                  </button>
                  <Slider
                    infinite
                    autoplay
                    ref={(el) => {
                      this.slider = el;
                    }}
                  >
                    {data.map((item, index) => (
                      <div className="how-it-works__point">
                        <div className="image">
                          <SVG src={item.icon} className="icon" />
                        </div>
                        <div className="content">
                          <p className="section-heading">
                            <span className="number">{index + 1}</span>
                            {item.title}
                          </p>

                          <TextContent>{item.content}</TextContent>
                        </div>
                      </div>
                    ))}
                  </Slider>
                  <button
                    type="button"
                    onClick={() => this.changeSlide(1)}
                    className="arrow arrow-right"
                  >
                    <SVG src="chevron-right" className="icon" />
                  </button>
                </div>
              )}
              <div className="col-md-4 col-xs-12">
                <div className="how-it-works__sidebar__item">
                  <div
                    className="how-it-works__lead how-it-works__lead--image"
                    style={{
                      backgroundImage:
                        "linear-gradient(rgba(0,0,0,0.2),rgba(0,0,0,0.2)), url('/static/images/video-placeholder-seller.jpg')",
                    }}
                  >
                    <p className="title">Looking to sell?</p>

                    <p className="desc">
                      Please call the Hammer Price Homes team on: <PhoneLink />
                    </p>
                  </div>

                  <div className="how-it-works__lead how-it-works__lead--blue">
                    <p className="title">Have any questions?</p>

                    <p className="desc">
                      Proceed to <Link to="faq/seller">Seller's FAQ</Link> and
                      learn more about the process.
                    </p>

                    <div className="icon">
                      <img
                        src="/static/images/icon-question-mark.png"
                        className="img-responsive"
                        alt="Have any questions?"
                      />
                    </div>
                  </div>

                  <div className="how-it-works__lead how-it-works__lead--green">
                    <p className="title">Interested in buying?</p>

                    <p className="desc">
                      Check out{" "}
                      <Link to="how-it-works/buyer">buyer instructions</Link> or
                      browse <Link to="properties">properties</Link>.
                    </p>

                    <div className="icon">
                      <img
                        src="/static/images/icon-i.png"
                        className="img-responsive"
                        alt="Interested in selling?"
                      />
                    </div>
                  </div>
                </div>

                <div className="how-it-works__sidebar__item informations-item">
                  <p className="section-heading">
                    Useful information{" "}
                    <SVG src="chevron-down" className="icon" />
                  </p>

                  <div className="how-it-works__sidebar__tabs">
                    <div className="how-it-works__sidebar__tabs__items">
                      <Tabs.Tab className="tab-item" name="tab-1" default>
                        <span>Anti sniping tool</span>
                      </Tabs.Tab>

                      <Tabs.Tab className="tab-item" name="tab-2">
                        <span>Anti gazumping</span>
                      </Tabs.Tab>

                      <Tabs.Tab className="tab-item" name="tab-3">
                        <span>Fees details</span>
                      </Tabs.Tab>
                    </div>

                    <Tabs.TabContent
                      className="tab-content"
                      name="tab-1"
                      default
                    >
                      <TextContent>
                        <p>
                          When a bid is placed in the closing minutes of an
                          auction it can prevent serious buyers missing out on a
                          purchase.
                        </p>

                        <p>
                          This practice of 'sniping' leaves both buyers and
                          sellers unsatisfied. For this reason we have developed
                          an anti-sniping tool and incorporated it into our
                          website.
                        </p>

                        <p>
                          If a bid is placed during the last 5 minutes of an
                          auction, the deadline will automatically be reset to 5
                          minutes, giving other bidders time to react. This will
                          continue until no further bids are placed.
                        </p>

                        <p>
                          Auctions often extend due to bids being placed during
                          anti sniping time. Please note that <b>new</b> bidders
                          cannot join an auction after the original scheduled
                          finish time.
                        </p>
                      </TextContent>
                    </Tabs.TabContent>

                    <Tabs.TabContent className="tab-content" name="tab-2">
                      <TextContent>
                        <p>
                          Our process closes on the final bid so won't allow for
                          gazumping. We will issue a 'Memorandum of Sale' once
                          our fee is received which is proof that a sale has
                          been agreed.
                        </p>

                        <p>
                          Although the seller isn't legally bound to sell by
                          this document, our T&C's mean that should a seller
                          give back word then they will incur a financial
                          penalty which has proven to significantly reduce the
                          risk of a sale that’s been agreed falling through.
                        </p>

                        <p>
                          Over 95% of all winning bids have been honoured by
                          Hammer Price Homes sellers.
                        </p>
                      </TextContent>
                    </Tabs.TabContent>

                    <Tabs.TabContent className="tab-content" name="tab-3">
                      <TextContent>
                        <p>
                          Our service is totally FREE to the seller, subject to
                          our terms and conditions. We charge the buyer a
                          reservation fee of 3.6% inc. VAT). Subject to a
                          minimum fee of £3,600 inc. VAT).
                        </p>
                      </TextContent>
                    </Tabs.TabContent>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <FeeCalculator />
        {!user.id && <CTASignUp type="seller" />}
        <Integrations />
      </Layout>
    );
  }
}

export default asPage(HowItWorksSeller);
